import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Edit3, Pencil } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Column from "@/components/board/Column";
import CardDetailModal from "@/components/board/CardDetailModal";
import MembersDialog from "@/components/board/MembersDialog";
import TrashDialog from "@/components/board/TrashDialog";
import { toast } from "sonner";
import { Clock, Plus, User as UserIcon, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function BoardDescEditor({ board, onBoardUpdated }) {
  const [editing, setEditing] = useState(false);
  const [desc, setDesc] = useState(board?.description || "");

  const save = async () => {
    const d = desc.trim();
    if (d === (board.description || "")) { setEditing(false); return; }
    try {
      await api.patch(`/boards/${board.id}`, { description: d });
      onBoardUpdated({ ...board, description: d });
      setEditing(false);
    } catch {
      setDesc(board.description || "");
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(); }}>
        <textarea
          autoFocus
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === "Escape" && (setDesc(board.description || ""), setEditing(false))}
          className="w-full text-sm bg-transparent border border-zinc-200 dark:border-zinc-700 focus:outline-none focus:border-black dark:focus:border-white text-zinc-500 dark:text-zinc-400 px-2 py-1.5 resize-none"
          rows={2}
          maxLength={500}
          placeholder="Add a board description..."
          data-testid="board-desc-input"
        />
      </form>
    );
  }

  return (
    <div className="group flex items-start gap-1.5">
      {desc ? (
        <>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed flex-1">{desc}</p>
          <button
            onClick={() => setEditing(true)}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 text-zinc-400 hover:text-zinc-900 dark:hover:text-white mt-0.5 shrink-0"
            aria-label="Edit description"
          >
            <Edit3 className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="text-xs text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors italic opacity-0 group-hover:opacity-100"
        >
          + Add description
        </button>
      )}
    </div>
  );
}

function BoardTitleEditor({ board, onBoardUpdated }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(board?.title || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = async () => {
    const t = title.trim();
    if (!t || t === board.title) { setEditing(false); return; }
    try {
      await api.patch(`/boards/${board.id}`, { title: t });
      onBoardUpdated({ ...board, title: t });
      setEditing(false);
    } catch {
      setTitle(board.title);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(); }} className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === "Escape" && (setTitle(board.title), setEditing(false))}
          className="font-display text-lg sm:text-xl font-bold tracking-tight bg-transparent border-b-2 border-black dark:border-white focus:outline-none text-zinc-950 dark:text-zinc-100 w-full max-w-md"
          maxLength={120}
          data-testid="board-title-input"
        />
      </form>
    );
  }

  return (
    <div className="flex items-center gap-2 group">
      <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight text-zinc-950 dark:text-zinc-100 truncate">
        {board?.title}
      </h2>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
        aria-label="Edit board title"
      >
        <Pencil className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function BoardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [lists, setLists] = useState([]);
  const [cards, setCards] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingList, setAddingList] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [openCardId, setOpenCardId] = useState(null);
  const draggingCardRef = useRef(null);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const scrollRef = useRef(null);
  const [activeColIdx, setActiveColIdx] = useState(0);
  const [filterOwner, setFilterOwner] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(""); // "" | "today" | "this_week" | "this_month" | "overdue" | "none"
  const [trashOpen, setTrashOpen] = useState(false);

  // Unique owners derived from cards for filtering
  const cardOwners = useMemo(() => {
    const owners = new Map();
    cards.forEach(c => {
      if (c.created_by_name) {
        if (!owners.has(c.created_by)) {
          owners.set(c.created_by, c.created_by_name);
        }
      }
    });
    return Array.from(owners.entries()).map(([id, name]) => ({ id, name }));
  }, [cards]);

  // Filtered cards based on search, owner, and start date
  const filteredCards = cards.filter(c => {
    // Owner filter
    if (filterOwner && c.created_by !== filterOwner) return false;

    // Name search
    if (searchQuery && !c.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;

    // Start date filter
    if (filterStartDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const start = c.start_date ? new Date(c.start_date + 'T00:00:00') : null;

      if (filterStartDate === "today") {
        if (!start || start.toDateString() !== today.toDateString()) return false;
      } else if (filterStartDate === "this_week") {
        if (!start) return false;
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        if (start < weekStart || start > weekEnd) return false;
      } else if (filterStartDate === "this_month") {
        if (!start || start.getMonth() !== today.getMonth() || start.getFullYear() !== today.getFullYear()) return false;
      } else if (filterStartDate === "overdue") {
        // Cards with start date before today that haven't been started (no due? actually started but past)
        // "Overdue" = start date is in the past but card isn't in "Done" list...
        // Simpler: start date is before today
        if (!start || start >= today) return false;
      } else if (filterStartDate === "none") {
        if (c.start_date) return false;
      } else if (filterStartDate === "upcoming") {
        if (!start || start <= today) return false;
      }
    }

    return true;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/boards/${id}`);
      setBoard(data.board);
      setLists(data.lists || []);
      setCards(data.cards || []);
      setMembers(data.members || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load board");
      if (e.response?.status === 404 || e.response?.status === 403) navigate("/boards");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  // Real-time updates via lightweight polling (every 6s when tab is visible
  // and no card detail modal is open). Hostinger Shared Hosting can't run
  // websockets, so polling is the portable fallback.
  useEffect(() => {
    let timer = null;
    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      if (openCardId) return; // pause while user edits a card
      try {
        const { data } = await api.get(`/boards/${id}`);
        setBoard(data.board);
        setLists(data.lists || []);
        setCards(data.cards || []);
        setMembers(data.members || []);
      } catch {
        /* swallow polling errors */
      }
    };
    timer = setInterval(tick, 6000);
    const onVis = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [id, openCardId]);

  // Track active column via scroll position (mobile pager dots)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const colWidth = el.clientWidth; // on mobile each column is full width
      const idx = Math.round(el.scrollLeft / colWidth);
      setActiveColIdx(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [lists.length]);

  const addList = async (e) => {
    e.preventDefault();
    const t = newListTitle.trim();
    if (!t) return;
    try {
      const { data } = await api.post(`/boards/${id}/lists`, { title: t });
      setLists((ls) => [...ls, data]);
      setNewListTitle("");
      setAddingList(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const duplicateList = async (listId) => {
    try {
      const { data } = await api.post(`/lists/${listId}/duplicate`);
      setLists((ls) => [...ls, data]);
      toast.success("List duplicated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const renameList = async (listId, title) => {
    setLists((ls) => ls.map((l) => (l.id === listId ? { ...l, title } : l)));
    try {
      await api.patch(`/lists/${listId}`, { title });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
      load();
    }
  };

  const deleteList = async (listId) => {
    setLists((ls) => ls.filter((l) => l.id !== listId));
    setCards((cs) => cs.filter((c) => c.list_id !== listId));
    try {
      await api.delete(`/lists/${listId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
      load();
    }
  };

  const addCard = async (listId, title) => {
    try {
      const { data } = await api.post(`/lists/${listId}/cards`, { title });
      setCards((cs) => [...cs, data]);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const onDragStartCard = (e, card) => {
    draggingCardRef.current = card;
    setDraggingCardId(card.id);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", card.id);
    } catch {
      /* ignore */
    }
  };

  const onDragEndCard = () => {
    draggingCardRef.current = null;
    setDraggingCardId(null);
  };

  const onDropCard = async (targetListId) => {
    const dragging = draggingCardRef.current;
    if (!dragging) return;
    if (dragging.list_id === targetListId) {
      draggingCardRef.current = null;
      setDraggingCardId(null);
      return;
    }
    const targetCards = cards.filter((c) => c.list_id === targetListId);
    const newPosition = targetCards.length;
    setCards((cs) =>
      cs.map((c) =>
        c.id === dragging.id ? { ...c, list_id: targetListId, position: newPosition } : c
      )
    );
    draggingCardRef.current = null;
    setDraggingCardId(null);
    try {
      await api.patch(`/cards/${dragging.id}`, {
        list_id: targetListId,
        position: newPosition,
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to move card");
      load();
    }
  };

  // Move card via menu (used on touch devices)
  const moveCardToList = async (cardId, targetListId) => {
    const card = cards.find((c) => c.id === cardId);
    if (!card || card.list_id === targetListId) return;
    const targetCards = cards.filter((c) => c.list_id === targetListId);
    const newPosition = targetCards.length;
    setCards((cs) =>
      cs.map((c) => (c.id === cardId ? { ...c, list_id: targetListId, position: newPosition } : c))
    );
    try {
      await api.patch(`/cards/${cardId}`, { list_id: targetListId, position: newPosition });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
      load();
    }
  };

  const onCardUpdated = (updated) => {
    setCards((cs) => cs.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const onCardDeleted = (cardId) => {
    setCards((cs) => cs.filter((c) => c.id !== cardId));
  };

  if (loading || !board) {
    return (
      <div className="h-screen flex flex-col">
        <Navbar back="/boards" />
        <div className="flex-1 flex items-center justify-center text-sm text-zinc-500 font-mono-tight uppercase tracking-widest">
          Loading board...
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white dark:bg-zinc-950">
      <Navbar
        title={board.title}
        back="/boards"
        right={
          <>
            <button
              onClick={() => setTrashOpen(true)}
              className="h-9 w-9 border border-zinc-200 dark:border-zinc-700 hover:border-black dark:hover:border-white transition-colors flex items-center justify-center shrink-0"
              aria-label="Recently deleted"
              data-testid="trash-button"
            >
              <Clock className="h-4 w-4 text-zinc-600 dark:text-zinc-400" />
            </button>
            <MembersDialog
              board={board}
              members={members}
              onMembersChange={setMembers}
              currentUser={user}
            />
          </>
        }
      />

      {/* Editable board title + description for owner */}
      <div className="px-3 sm:px-6 py-3 border-b border-zinc-100 dark:border-zinc-800/50 bg-white dark:bg-zinc-950 shrink-0">
        {board && user && user.id === board.owner_id ? (
          <div className="space-y-1">
            <BoardTitleEditor board={board} onBoardUpdated={(b) => setBoard(s => ({...s, title: b.title}))} />
            <BoardDescEditor board={board} onBoardUpdated={(b) => setBoard(s => ({...s, description: b.description}))} />
          </div>
        ) : (
          <div>
            <h2 className="font-display text-lg sm:text-xl font-bold tracking-tight text-zinc-950 dark:text-zinc-100 truncate">
              {board?.title}
            </h2>
            {board?.description && (
              <p className="text-sm text-zinc-500 mt-0.5 truncate">{board.description}</p>
            )}
          </div>
        )}
      </div>

      {/* Filter bar + Mobile pager dots */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 shrink-0">
        {/* Search + filters */}
        <div className="flex flex-wrap items-center gap-2 px-3 sm:px-6 py-2 border-b border-zinc-100 dark:border-zinc-800/50">
          {/* Name search */}
          <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-initial">
            <svg className="h-3.5 w-3.5 text-zinc-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search cards..."
              className="w-full sm:w-40 text-xs bg-transparent border border-zinc-200 dark:border-zinc-700 px-2 py-1.5 focus:outline-none focus:border-black dark:focus:border-white text-zinc-700 dark:text-zinc-300 placeholder-zinc-400"
              data-testid="card-search-input"
            />
          </div>

          {/* Owner filter */}
          {cardOwners.length > 0 && (
            <>
              <UserIcon className="h-3.5 w-3.5 text-zinc-400 shrink-0 hidden sm:block" />
              <select
                value={filterOwner}
                onChange={(e) => setFilterOwner(e.target.value)}
                className="text-xs font-medium bg-transparent border border-zinc-200 dark:border-zinc-700 px-2 py-1.5 focus:outline-none focus:border-black dark:focus:border-white text-zinc-700 dark:text-zinc-300"
                data-testid="owner-filter"
              >
                <option value="">All owners</option>
                {cardOwners.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </>
          )}

          {/* Start date filter */}
          <select
            value={filterStartDate}
            onChange={(e) => setFilterStartDate(e.target.value)}
            className="text-xs font-medium bg-transparent border border-zinc-200 dark:border-zinc-700 px-2 py-1.5 focus:outline-none focus:border-black dark:focus:border-white text-zinc-700 dark:text-zinc-300"
            data-testid="start-date-filter"
          >
            <option value="">Any start</option>
            <option value="today">Today</option>
            <option value="this_week">This week</option>
            <option value="this_month">This month</option>
            <option value="upcoming">Upcoming</option>
            <option value="overdue">Overdue</option>
            <option value="none">No start date</option>
          </select>

          {/* Clear all filters */}
          {(searchQuery || filterOwner || filterStartDate) && (
            <button
              onClick={() => { setSearchQuery(""); setFilterOwner(""); setFilterStartDate(""); }}
              className="text-xs text-zinc-500 hover:text-black dark:hover:text-white transition-colors whitespace-nowrap"
            >
              Clear all
            </button>
          )}

          <span className="text-[10px] text-zinc-400 ml-auto hidden sm:block">
            {(searchQuery || filterOwner || filterStartDate) ? filteredCards.length + ' / ' + cards.length + ' cards' : cards.length + ' cards'}
          </span>
        </div>

        {/* Mobile pager dots */}
        {lists.length > 1 && (
          <div className="sm:hidden flex items-center justify-center gap-1.5 py-2">
            {lists.map((l, i) => (
              <button
                key={l.id}
                type="button"
                onClick={() => {
                  const el = scrollRef.current;
                  if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
                }}
                aria-label={`Go to column ${i + 1}`}
                aria-current={i === activeColIdx ? "true" : undefined}
                className={`h-2 transition-all ${
                  i === activeColIdx ? "w-8 bg-zinc-900 dark:bg-white" : "w-2 bg-zinc-300 dark:bg-zinc-600"
                }`}
                data-testid={`pager-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        className="flex-1 flex overflow-x-auto overflow-y-hidden bg-zinc-50/40 dark:bg-zinc-900/20 snap-x snap-mandatory sm:snap-none"
        data-testid="board-canvas"
      >
        {lists.map((list) => (
          <Column
            key={list.id}
            list={list}
            lists={lists}
            cards={filteredCards.filter((c) => c.list_id === list.id).sort((a, b) => a.position - b.position)}
            onAddCard={addCard}
            onCardClick={(c) => setOpenCardId(c.id)}
            onDeleteList={deleteList}
            onDuplicateList={duplicateList}
            onRenameList={renameList}
            onDragStartCard={onDragStartCard}
            onDragEndCard={onDragEndCard}
            onDropCard={onDropCard}
            onMoveCard={moveCardToList}
            draggingCardId={draggingCardId}
          />
        ))}

        <div className="w-full sm:w-[320px] min-w-full sm:min-w-[320px] p-3 shrink-0 snap-start sm:snap-align-none">
          {addingList ? (
            <form
              onSubmit={addList}
              className="border border-black dark:border-white bg-white dark:bg-zinc-950 p-3"
              data-testid="add-list-form"
            >
              <input
                autoFocus
                value={newListTitle}
                onChange={(e) => setNewListTitle(e.target.value)}
                placeholder="List title..."
                className="w-full text-base sm:text-sm font-bold uppercase tracking-[0.1em] border-b border-zinc-200 dark:border-zinc-700 focus:outline-none focus:border-black dark:focus:border-white py-2 bg-transparent text-zinc-950 dark:text-zinc-100"
                data-testid="add-list-input"
              />
              <div className="flex items-center gap-2 mt-3">
                <button
                  type="submit"
                  className="bg-black dark:bg-white text-white dark:text-black px-3 py-2 text-xs font-medium hover:bg-zinc-800 dark:hover:bg-zinc-200"
                  data-testid="add-list-submit"
                >
                  Add list
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddingList(false);
                    setNewListTitle("");
                  }}
                  className="p-2 text-zinc-500 hover:text-black dark:hover:text-white"
                  aria-label="Cancel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </form>
          ) : (
            <button
              onClick={() => setAddingList(true)}
              className="w-full border border-dashed border-zinc-300 dark:border-zinc-600 py-6 sm:py-4 text-sm text-zinc-500 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white active:border-black transition-colors flex items-center justify-center gap-2"
              data-testid="add-list-button"
            >
              <Plus className="h-4 w-4" /> Add another list
            </button>
          )}
        </div>
      </div>

      <CardDetailModal
        open={!!openCardId}
        onOpenChange={(v) => !v && setOpenCardId(null)}
        cardId={openCardId}
        lists={lists}
        onUpdated={onCardUpdated}
        onDeleted={onCardDeleted}
        onMoveCard={moveCardToList}
        currentUser={user}
        board={board}
      />

      <TrashDialog
        open={trashOpen}
        onOpenChange={setTrashOpen}
        boardId={id}
        currentUser={user}
        board={board}
        onCardRestored={() => load()}
      />
    </div>
  );
}
